import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Star, Loader, ArrowLeft } from 'lucide-react';
import { supabase } from '../../../lib/supabase';
import { useNotification } from '../../../context/NotificationContext';
import { reviewSchema, type ReviewFormData } from '../../../lib/validation';
import FormError from '../../../components/FormError';

interface Booking {
  id: string;
  services: {
    name: string;
  };
  booking_date: string;
}

export default function ReviewForm() {
  const { bookingId } = useParams();
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  
  const [booking, setBooking] = useState<Booking | null>(null);
  const [formData, setFormData] = useState<ReviewFormData>({
    rating: 5,
    comment: '',
  });
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [errors, setErrors] = useState<Partial<Record<keyof ReviewFormData, string>>>({});

  useEffect(() => {
    const fetchBooking = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) {
          navigate('/login');
          return;
        }

        // First check if a review already exists
        const { data: existingReview } = await supabase
          .from('reviews')
          .select('id')
          .eq('booking_id', bookingId)
          .single();

        if (existingReview) {
          showNotification('error', 'You have already submitted a review for this booking');
          navigate('/dashboard');
          return;
        }

        const { data, error } = await supabase
          .from('bookings')
          .select(`
            id,
            booking_date,
            services (
              name
            )
          `)
          .eq('id', bookingId)
          .eq('user_id', session.user.id)
          .single();

        if (error) throw error;
        if (!data) throw new Error('Booking not found');

        setBooking(data);
      } catch (error) {
        console.error('Error fetching booking:', error);
        showNotification('error', 'Failed to load booking details');
        navigate('/dashboard');
      } finally {
        setInitialLoading(false);
      }
    };

    fetchBooking();
  }, [bookingId, navigate, showNotification]);

  const validateForm = () => {
    try {
      reviewSchema.parse(formData);
      setErrors({});
      return true;
    } catch (error) {
      if (error instanceof Error) {
        const zodError = JSON.parse(error.message);
        const newErrors: typeof errors = {};
        zodError.forEach((err: any) => {
          const path = err.path[0] as keyof ReviewFormData;
          newErrors[path] = err.message;
        });
        setErrors(newErrors);
      }
      return false;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!validateForm()) {
      showNotification('error', 'Please fix the form errors before submitting');
      return;
    }

    setLoading(true);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('No session found');

      // Check again if a review already exists
      const { data: existingReview } = await supabase
        .from('reviews')
        .select('id')
        .eq('booking_id', bookingId)
        .single();

      if (existingReview) {
        throw new Error('You have already submitted a review for this booking');
      }

      // Insert review
      const { error: reviewError } = await supabase
        .from('reviews')
        .insert({
          booking_id: bookingId,
          user_id: session.user.id,
          rating: formData.rating,
          comment: formData.comment
        });

      if (reviewError) throw reviewError;

      // Update booking status to completed
      const { error: bookingError } = await supabase
        .from('bookings')
        .update({ status: 'completed' })
        .eq('id', bookingId);

      if (bookingError) throw bookingError;

      showNotification('success', 'Thank you for your review!');
      navigate('/dashboard');
    } catch (error) {
      const message = error instanceof Error ? error.message : 'An error occurred while submitting the review';
      setError(message);
      showNotification('error', message);
    } finally {
      setLoading(false);
    }
  };

  if (initialLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!booking) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="container mx-auto px-4">
        <div className="max-w-2xl mx-auto">
          <button
            onClick={() => navigate('/dashboard')}
            className="mb-6 text-gray-600 hover:text-gray-900 transition-colors flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Dashboard
          </button>

          <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4">Service Details</h2>
            <div className="space-y-2 text-gray-600">
              <p>
                <span className="font-medium">Service:</span> {booking.services.name}
              </p>
              <p>
                <span className="font-medium">Date:</span>{' '}
                {new Date(booking.booking_date).toLocaleDateString('en-US', {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                })}
              </p>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm p-6">
            <h1 className="text-2xl font-bold mb-6">Write a Review</h1>

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg mb-6">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Rating
                </label>
                <div className="flex gap-2">
                  {[1, 2, 3, 4, 5].map((value) => (
                    <button
                      key={value}
                      type="button"
                      onClick={() => setFormData({ ...formData, rating: value })}
                      className="focus:outline-none transition-transform hover:scale-110"
                    >
                      <Star
                        className={`w-8 h-8 ${
                          value <= formData.rating
                            ? 'text-yellow-400 fill-current'
                            : 'text-gray-300'
                        }`}
                      />
                    </button>
                  ))}
                </div>
                {errors.rating && <FormError message={errors.rating} />}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Your Review
                </label>
                <textarea
                  value={formData.comment}
                  onChange={(e) => setFormData({ ...formData, comment: e.target.value })}
                  rows={4}
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 ${
                    errors.comment ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="Share your experience with the service..."
                />
                {errors.comment && <FormError message={errors.comment} />}
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <>
                    <Loader className="w-5 h-5 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <Star className="w-5 h-5" />
                    Submit Review
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}